/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { requestPasswordResetEmail } from '@/auth/api';
import { checkIsFunction } from '@/auth/lib/checkIsFunction';
import { getFormValues } from '@/auth/lib/getFormValues';
import { UseResetPasswordFormProps } from '@/auth/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import { useCallback, useState } from 'preact/hooks';
import { validateEmail } from '@/auth/lib/validationFields';

export const useResetPasswordForm = ({
  routeSignIn,
  onErrorCallback,
  onSuccessCallback,
  setActiveComponent,
  handleSetInLineAlertProps,
}: UseResetPasswordFormProps) => {
  const translations = useText({
    successPasswordResetEmailNotification:
      'Auth.Notification.successPasswordResetEmailNotification',
  });
  const [isLoading, setIsLoading] = useState(false);

  const submitResetPassword = useCallback(
    async (event: any): Promise<void> => {
      event.preventDefault();

      const formValues = getFormValues(event.target);

      if (!validateEmail(formValues?.email)) {
        return;
      }

      setIsLoading(true);

      const response = await requestPasswordResetEmail(formValues.email);

      setIsLoading(false);

      if (!response.success) {
        onErrorCallback?.(response);
        handleSetInLineAlertProps?.({
          type: 'error',
          text: response.message,
        });
        return;
      }

      if (onSuccessCallback) {
        await onSuccessCallback();
        return;
      }

      handleSetInLineAlertProps?.({
        type: 'success',
        text: translations.successPasswordResetEmailNotification.replace(
          '{email}',
          formValues.email
        ),
      });
    },
    [
      handleSetInLineAlertProps,
      onErrorCallback,
      onSuccessCallback,
      translations.successPasswordResetEmailNotification,
    ]
  );

  const redirectToSignInPage = useCallback(() => {
    if (checkIsFunction(setActiveComponent)) {
      setActiveComponent('signInForm');
      return;
    }

    if (checkIsFunction(routeSignIn)) {
      window.location.href = routeSignIn();
    }
  }, [setActiveComponent, routeSignIn]);

  return {
    isLoading,
    submitResetPassword,
    redirectToSignInPage,
  };
};
